import { createSlice } from '@reduxjs/toolkit';
import { fetchAiRanking } from './actions';
import {
  AgentAiRankingEntry,
  AiRankingExperimentArm,
  AiRankingTier,
} from '../../interfaces/dealAiAgentRanking';

export interface AiRankingAgentInfo {
  /** 1-based position of the agent in the ranking array. */
  rank: number;
  /** LLM arm only. */
  tier?: AiRankingTier;
  /** LLM arm only. */
  reason?: string;
  /** Match-score arm only. */
  matchScore?: number;
  /** Match-score arm only. */
  agentScore?: number;
}

export type AiRankingByAgentIdMap = Record<number, AiRankingAgentInfo>;

export interface AiRankingState {
  loading: boolean;
  /** True once a fetch settled (including 404 "no ranking yet"). */
  loaded: boolean;
  error: string | null;
  /** Deal id the current data was requested for; guards one fetch per deal. */
  dealId: string | null;
  /** null = no ranking exists (404) / not fetched / fetch failed → no tabs. */
  arm: AiRankingExperimentArm | null;
  entries: AgentAiRankingEntry[];
  byAgentId: AiRankingByAgentIdMap;
  llmModel: string | null;
}

const initialState: AiRankingState = {
  loading: false,
  loaded: false,
  error: null,
  dealId: null,
  arm: null,
  entries: [],
  byAgentId: {},
  llmModel: null,
};

const aiRankingSlice = createSlice({
  name: 'aiRanking',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchAiRanking.pending, (state, { meta }) => {
      state.loading = true;
      state.loaded = false;
      state.error = null;
      state.dealId = String(meta.arg);
      state.arm = null;
      state.entries = [];
      state.byAgentId = {};
      state.llmModel = null;
    });
    builder.addCase(fetchAiRanking.fulfilled, (state, { payload, meta }) => {
      // Ignore a response for a deal we're no longer viewing: if the deal was
      // swapped mid-flight, `pending` has already reset state.dealId to the
      // newest request, so an older fulfilled must not clobber current data.
      if (String(meta.arg) !== state.dealId) return;
      state.loading = false;
      state.loaded = true;
      // null payload = 404: no ranking exists yet — treat as control (no tabs)
      if (!payload) {
        state.arm = null;
        return;
      }
      const entries = payload.ranking ?? [];
      state.arm = payload.experimentArm;
      state.entries = entries;
      state.byAgentId = entries.reduce((acc, entry, index) => {
        acc[entry.agent_id] = {
          rank: index + 1,
          tier: entry.tier,
          reason: entry.reason,
          matchScore: entry.match_score,
          agentScore: entry.agent_score,
        };
        return acc;
      }, {} as AiRankingByAgentIdMap);
      state.llmModel = payload.llmModel ?? null;
    });
    builder.addCase(fetchAiRanking.rejected, (state, { payload, meta }) => {
      // Same stale-deal guard as `fulfilled`: a late rejection for a swapped
      // deal must not surface an error over the current deal's state.
      if (String(meta.arg) !== state.dealId) return;
      state.loading = false;
      state.loaded = true;
      state.error = payload ?? 'Failed to fetch AI ranking';
      state.arm = null;
    });
  },
});

export default aiRankingSlice.reducer;
